import { NodeTypes } from '@xyflow/react';

import { NodeConfig } from '../types/node-config';

/**
 * Factory function to create a node configuration with type inference.
 * Provides runtime validation and helpful development experience.
 *
 * @example
 * ```typescript
 * export const specializedAgentConfig = defineNode({
 *   description: {
 *     displayName: 'Specialized Agent',
 *     name: 'specialized',
 *     // ...
 *   },
 *   render: memo(BaseNodeRenderer),
 *   onAsyncSave: async (nodeId, data, context) => { ... }
 * });
 * ```
 */
export function defineNode(config: NodeConfig): NodeConfig {
  // Runtime validation
  if (!config.description?.name) {
    throw new Error('Node config must have description.name');
  }

  if (!config.description?.displayName) {
    throw new Error('Node config must have description.displayName');
  }

  // Return validated config
  return config;
}

/**
 * Convert node configs to ReactFlow NodeTypes
 */
export function buildReactFlowNodeTypes(configs: Record<string, NodeConfig>): NodeTypes {
  const nodeTypes: NodeTypes = {};

  Object.entries(configs).forEach(([key, config]) => {
    if (config.render) {
      nodeTypes[key] = config.render;
    }
  });

  return nodeTypes;
}
